import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Stethoscope, Trash2 } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { api, errMessage } from "../lib/api";

// Per-child professional access: invite by email, list/revoke, guidance & activity.
export const ProfessionalsPanel = ({ child }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [activity, setActivity] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/children/${child.child_id}/pro-access`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/children/${child.child_id}/pro-guidance`).then((r) => setGuidance(r.data)).catch(() => {});
    api.get(`/children/${child.child_id}/pro-activity`).then((r) => setActivity(r.data.activity)).catch(() => {});
  }, [child.child_id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: res } = await api.post(`/children/${child.child_id}/pro-invites`, { professional_email: inviteEmail });
      setInviteResult(res);
      setInviteEmail("");
      load();
      toast.success(res.email_sent ? "Invitation emailed" : "Invite created — share the code yourself");
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSending(false);
    }
  };

  const revoke = async (accessId) => {
    try {
      await api.delete(`/children/${child.child_id}/pro-access/${accessId}`);
      toast.success("Access revoked immediately");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full rounded-lg gap-2 text-muted-foreground" data-testid={`professionals-panel-${child.child_id}`}>
          <Stethoscope className="h-4 w-4" aria-hidden="true" /> Professionals
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="professionals-dialog">
        <DialogHeader>
          <DialogTitle>Speech professionals for {child.display_name}</DialogTitle>
          <DialogDescription>
            Invite a professional to view {child.display_name}'s progress (read-only, 90-day access,
            revocable anytime). They never see chats, stories, recordings or the memory console.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={invite} className="flex gap-2">
          <Input type="email" required placeholder="professional@clinic.com" value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)} className="h-10" data-testid="pro-invite-email-input" />
          <Button type="submit" disabled={sending} className="rounded-lg shrink-0" data-testid="pro-invite-send-button">
            {sending ? "Sending…" : "Invite"}
          </Button>
        </form>

        {inviteResult && (
          <div className="rounded-xl bg-accent p-3 space-y-2" data-testid="pro-invite-result">
            <p className="text-xs font-semibold">
              One-time code for {inviteResult.professional_email} (expires in 7 days{inviteResult.email_sent ? ", also emailed" : " — email could not be sent, share it yourself"}):
            </p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-bold bg-card rounded-lg px-3 py-1.5 flex-1 break-all" data-testid="pro-invite-code">{inviteResult.code}</code>
              <Button variant="outline" size="sm" className="rounded-lg shrink-0"
                onClick={() => { navigator.clipboard.writeText(inviteResult.code); toast.success("Code copied"); }}
                data-testid="pro-invite-copy-button">
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Active access</p>
          {!data || data.access.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="pro-access-empty">No professional has access right now.</p>
          ) : data.access.map((a) => (
            <div key={a.access_id} className="flex items-center justify-between gap-2 rounded-xl border border-border p-3" data-testid={`pro-access-${a.access_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{a.professional_name || a.professional_email}</p>
                <p className="text-xs text-muted-foreground">Progress only · until {new Date(a.expires_at).toLocaleDateString()}</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-full text-destructive border-destructive/40 shrink-0"
                onClick={() => revoke(a.access_id)} data-testid={`pro-revoke-${a.access_id}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Revoke
              </Button>
            </div>
          ))}
        </div>

        {guidance && (guidance.goals.length > 0 || guidance.reports.length > 0) && (
          <div className="space-y-2" data-testid="pro-guidance">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Guidance from professionals</p>
            {guidance.goals.map((g) => (
              <div key={g.goal_id} className="rounded-xl border border-border p-3 space-y-1" data-testid={`pro-goal-${g.goal_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{g.title}</p>
                  <Badge variant="secondary" className="text-[10px] shrink-0">{g.target_area}</Badge>
                </div>
                {g.words.length > 0 && <p className="text-xs text-muted-foreground">Practice words: {g.words.join(", ")}</p>}
                {g.note_for_parent && <p className="text-xs text-muted-foreground italic">"{g.note_for_parent}" — {g.professional_name}</p>}
              </div>
            ))}
            {guidance.reports.map((r) => (
              <div key={r.report_id} className="rounded-xl border border-border p-3 space-y-1" data-testid={`pro-report-${r.report_id}`}>
                <p className="text-sm font-semibold">{r.title}</p>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{r.body}</p>
                <p className="text-[10px] text-muted-foreground/70">{r.professional_name} · {new Date(r.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Professional activity (visible to you)</p>
          {!activity || activity.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="pro-activity-empty">No professional activity yet.</p>
          ) : (
            <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="pro-activity-list">
              {activity.map((a, i) => (
                <li key={`${a.ts}-${i}`} className="text-xs text-muted-foreground">
                  {new Date(a.ts).toLocaleString()} — {a.actor_name || "Professional"}: {a.action.replace("pro.", "").replace(/[._]/g, " ")}
                  {a.decision === "deny" ? " (denied)" : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
